import {
  CheckCircleOutlineOutlined,
  ErrorOutlined,
  InfoOutlined,
  WarningOutlined,
} from "@material-ui/icons";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import React, { Suspense } from "react";
import ReactDOM from "react-dom/client";
import { Toaster } from "sonner";
import App from "./App";
import { EditorProvider } from "./components/form/editor/plugins/EditorProvider";
import { LoginValidator } from "./context/LoginValidator";
import { ModalsStateProvider } from "./context/ModalsContext";
import { NavigationProvider } from "./context/NavigationContext";

import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
} from "react-router-dom";
import RedirectBasedOnAuth from "./components/auth/RedirectBasedOnAuth";
import { RequireAuth } from "./components/auth/RequireAuth";
import { RequireUnauth } from "./components/auth/RequireUnauth";
import "./index.css";

const ActivateAICreditPage = React.lazy(
  () => import("./components/ActivateAICreditsPage")
);
const LoginPage = React.lazy(() => import("./components/LoginPage"));
const RegisterPage = React.lazy(() => import("./components/RegisterPage"));
const DashboardPage = React.lazy(() => import("./components/DashboardPage"));
const DownloadEntries = React.lazy(
  () => import("./components/DownloadEntries")
);
const ExportPage = React.lazy(() => import("./components/ExportPage"));
const RandomizeEntriesWizard = React.lazy(
  () => import("./components/RandomizeEntriesWizard")
);
const CompleteEntryPage = React.lazy(
  () => import("./components/CompleteEntryPage")
);
const SettingsPage = React.lazy(() => import("./components/SettingsPage"));
const ImportICalPage = React.lazy(() => import("./components/ImportICalPage"));
const ForgotPassword = React.lazy(() => import("./components/ForgotPassword"));

const queryClient = new QueryClient();

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

export const routes = {
  routes: [
    {
      path: "login",
      element: (
        <Suspense
          fallback={
            <div className={"flex w-full justify-center items-center h-32"}>
              <button className="btn btn-circle loading"></button>
            </div>
          }
        >
          <LoginPage />
        </Suspense>
      ),
    },
    {
      path: "forgot",
      element: (
        <Suspense
          fallback={
            <div className={"flex w-full justify-center items-center h-32"}>
              <button className="btn btn-circle loading"></button>
            </div>
          }
        >
          <ForgotPassword />
        </Suspense>
      ),
    },
    {
      path: "register",
      element: (
        <Suspense
          fallback={
            <div className={"flex w-full justify-center items-center h-32"}>
              <button className="btn btn-circle loading"></button>
            </div>
          }
        >
          <RegisterPage />
        </Suspense>
      ),
    },
  ],
  protectedRoutes: [
    {
      path: "download",
      element: (
        <Suspense
          fallback={
            <div className={"flex w-full justify-center items-center h-32"}>
              <button className="btn btn-circle loading"></button>
            </div>
          }
        >
          <DownloadEntries />
        </Suspense>
      ),
    },
    {
      path: "randomize",
      element: (
        <Suspense
          fallback={
            <div className={"flex w-full justify-center items-center h-32"}>
              <button className="btn btn-circle loading"></button>
            </div>
          }
        >
          <RandomizeEntriesWizard />
        </Suspense>
      ),
    },
    {
      path: "export",
      element: (
        <Suspense
          fallback={
            <div className={"flex w-full justify-center items-center h-32"}>
              <button className="btn btn-circle loading"></button>
            </div>
          }
        >
          <ExportPage />
        </Suspense>
      ),
    },
    {
      path: "dashboard",
      element: (
        <Suspense
          fallback={
            <div className={"flex w-full justify-center items-center h-32"}>
              <button className="btn btn-circle loading"></button>
            </div>
          }
        >
          <DashboardPage />
        </Suspense>
      ),
    },
    {
      path: "entryCompletion",
      element: (
        <Suspense
          fallback={
            <div className={"flex w-full justify-center items-center h-32"}>
              <button className="btn btn-circle loading"></button>
            </div>
          }
        >
          <CompleteEntryPage />
        </Suspense>
      ),
    },
    {
      path: "importICal",
      element: (
        <Suspense
          fallback={
            <div className={"flex w-full justify-center items-center h-32"}>
              <button className="btn btn-circle loading"></button>
            </div>
          }
        >
          <ImportICalPage />
        </Suspense>
      ),
    },
    {
      path: "settings",
      element: (
        <Suspense
          fallback={
            <div className={"flex w-full justify-center items-center h-32"}>
              <button className="btn btn-circle loading"></button>
            </div>
          }
        >
          <SettingsPage />
        </Suspense>
      ),
    },
    {
      path: "activate",
      element: (
        <Suspense
          fallback={
            <div className={"flex w-full justify-center items-center h-32"}>
              <button className="btn btn-circle loading"></button>
            </div>
          }
        >
          <ActivateAICreditPage />
        </Suspense>
      ),
    },
  ],
};

const router = createBrowserRouter([
  {
    path: "/",
    element: (
      <LoginValidator>
        <NavigationProvider>
          <ModalsStateProvider>
            <EditorProvider>
              <App />
            </EditorProvider>
          </ModalsStateProvider>
        </NavigationProvider>
      </LoginValidator>
    ),
    children: [
      {
        element: <RequireAuth />,
        children: routes.protectedRoutes,
      },
      {
        element: <RequireUnauth />,
        children: routes.routes,
      },
    ],
  },
  {
    index: true,
    element: <RedirectBasedOnAuth />,
  },
  { path: "*", element: <Navigate to={"dashboard"} replace /> },
]);

root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
      <Toaster
        position="bottom-right"
        visibleToasts={2}
        closeButton={true}
        expand={true}
        icons={{
          success: <CheckCircleOutlineOutlined />,
          error: <ErrorOutlined />,
          info: <InfoOutlined />,
          warning: <WarningOutlined />,
        }}
        toastOptions={{
          unstyled: true,
          classNames: {
            title: "text-lg",
            description: "text-sm",
            content: "flex items-center",
            default: "alert alert-info",
            error: "alert alert-error",
            success: "alert alert-success",
            info: "alert alert-info",
            warning: "alert alert-warning",
            actionButton: "btn !btn-secondary btn-xs",
            closeButton:
              "btn btn-circle !btn-secondary btn-xs border-1 border-base-300",
          },
        }}
      />
    </QueryClientProvider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function

// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
// reportWebVitals();
